/**
 * Client for agent update rollouts (release 3.2 patch management, phase 2).
 * Wire shapes mirror apps/api-controller/src/modules/edge/update-rollouts.ts.
 * Kept out of the large api.ts so parallel work on that file does not collide.
 *
 *   GET  /v1/edge/updates                          hold, rollouts, withdrawn versions
 *   POST /v1/edge/updates/rollouts                 create (operator)
 *   GET  /v1/edge/updates/rollouts/:id             detail, stages, gate, preview, rollback plan
 *   GET  /v1/edge/updates/rollouts/:id/devices     per-device membership
 *   POST /v1/edge/updates/rollouts/:id/submit      operator
 *   POST /v1/edge/updates/rollouts/:id/approve     approver, never the creator (four eyes)
 *   POST /v1/edge/updates/rollouts/:id/pause       operator
 *   POST /v1/edge/updates/rollouts/:id/resume      approver
 *   POST /v1/edge/updates/rollouts/:id/cancel      operator
 *   POST /v1/edge/updates/rollouts/:id/rollback    approver
 *   POST /v1/edge/updates/hold                     operator: HOLD ALL
 *   POST /v1/edge/updates/hold/release             approver
 *   POST /v1/edge/updates/withdrawn                approver: withdraw a version
 *   POST /v1/edge/updates/withdrawn/:v/reinstate   approver: propose reinstating it
 *   POST /v1/edge/updates/withdrawn/:v/reinstate/confirm  a DIFFERENT approver confirms
 */

import { apiFetch, newIdempotencyKey } from './api';

/** The operator's permission: prepare, submit, and only ever STOP (pause, cancel, hold). */
export const UPDATE_WRITE = 'edge:update:write';
/** The approver's permission: start (not your own), resume, release the hold, roll back, withdraw. */
export const UPDATE_APPROVE = 'edge:update:approve';

export type RolloutState =
  | 'draft'
  | 'awaiting_approval'
  | 'running'
  | 'paused'
  | 'halted'
  | 'completed'
  | 'rolled_back'
  | 'cancelled';

export type RolloutMemberStatus =
  | 'instructed'
  | 'updated'
  | 'failed'
  | 'rolled_back'
  | 'managed_externally'
  | 'stood_down'
  | 'gone'
  | 'rollback_requested'
  | 'reverted'
  | 'rollback_failed';

export type RolloutPilot =
  | { type: 'devices'; podIds: string[]; scopeId: null }
  | { type: 'install_token'; podIds: string[]; scopeId: string }
  | { type: 'business_unit'; podIds: string[]; scopeId: string };

export interface Rollout {
  id: string;
  targetVersion: string;
  platforms: string[];
  arch: string;
  pilot: RolloutPilot;
  waves: number[];
  pilotSoakHours: number;
  waveSoakHours: number;
  minReportedPercent: number;
  updateWindow: string | null;
  reason: string;
  state: RolloutState;
  stateReason: string | null;
  currentStage: number | null;
  currentStageLabel: string | null;
  stageStartedAt: string | null;
  createdBy: string;
  createdAt: string;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  startedAt: string | null;
  pausedBy: string | null;
  pausedAt: string | null;
  haltedAt: string | null;
  completedAt: string | null;
  rolledBackBy: string | null;
  rolledBackAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  updatedAt: string;
}

export interface UpdateHold {
  held: boolean;
  reason: string | null;
  setBy: string | null;
  at: string | null;
}

export interface WithdrawnVersion {
  version: string;
  reason: string;
  withdrawnBy: string;
  at: string;
  /** A pending proposal to reinstate it; a different approver confirms. */
  reinstateProposal: { by: string; at: string; reason: string } | null;
}

/** Reinstated in the console (last 30 days): devices refuse it until a signed list without it is published. */
export interface ReinstatedVersion {
  version: string;
  reason: string;
  proposedBy: string;
  reinstatedBy: string;
  at: string;
  /** Sign into <dir>, then publish onto the downloads folder (publish-withdrawn). */
  publisherCommands: string[];
}

export interface UpdatesOverview {
  hold: UpdateHold;
  rollouts: Rollout[];
  withdrawn: WithdrawnVersion[];
  reinstated: ReinstatedVersion[];
  /** The agent download folder (FOCTTA_ARTIFACT_BASE_URL); null when the deployment has not declared one. */
  downloadsBase: string | null;
  /** False for a caller limited to some business units: rollouts span units, so none are listed. */
  fleetWideCaller: boolean;
}

export type StatusCounts = Record<RolloutMemberStatus, number>;

export interface RolloutStage {
  stage: number;
  label: string;
  percent: number;
  soakHours: number;
  counts: StatusCounts;
  /** What the gate counts; `failed` keeps a failure through a stand-down or a resume. */
  gate: { population: number; updated: number; failed: number; rolledBack: number };
}

export type RolloutGate =
  | { action: 'halt'; reason: string; stage: number }
  | { action: 'hold'; reason: string }
  | { action: 'advance'; to: number; reason: string }
  | { action: 'complete'; reason: string };

export interface RolloutPreview {
  endpointAgents: number;
  eligible: number;
  atTarget: number;
  aboveTarget: number;
  tooOld: number;
  unrecognisedVersion: number;
  pilot: { matched: number; eligible: number; atTarget: number; notFound: string[] };
  waves: Array<{ stage: number; label: string; percent: number; eligible: number }>;
}

export interface RolloutDetail {
  rollout: Rollout;
  stages: RolloutStage[];
  gate: RolloutGate | null;
  hold: UpdateHold;
  preview: RolloutPreview | null;
  rollbackPlan: { fromVersions: string[]; devices: number; commands: string[] };
}

export interface RolloutDevice {
  podId: string;
  podRowId: string | null;
  displayName: string | null;
  hostname: string | null;
  stage: number;
  stageLabel: string;
  fromVersion: string;
  version: string | null;
  status: RolloutMemberStatus;
  lastState: string | null;
  lastReason: string | null;
  lastReportAt: string | null;
  instructedAt: string | null;
  outcomeAt: string | null;
  rollbackInstructedAt: string | null;
  lastHeartbeatAt: string | null;
}

export interface CreateRolloutInput {
  targetVersion: string;
  platforms: Array<'windows' | 'linux'>;
  pilot:
    | { type: 'devices'; podIds: string[] }
    | { type: 'install_token'; installTokenId: string }
    | { type: 'business_unit'; businessUnitId: string };
  waves: number[];
  pilotSoakHours: number;
  waveSoakHours: number;
  minReportedPercent: number;
  updateWindow: string | null;
  reason: string;
  submit: boolean;
}

export interface StandDownResult {
  stoodDown: number;
  inFlight: number;
  cannot: number;
}

const post = <T>(path: string, body: unknown = {}): Promise<T> =>
  apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body), idempotencyKey: newIdempotencyKey() });

export const getUpdatesOverview = (): Promise<UpdatesOverview> => apiFetch('/v1/edge/updates');

export const getRollout = (id: string): Promise<RolloutDetail> =>
  apiFetch(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}`);

export function listRolloutDevices(
  id: string,
  q: { status?: RolloutMemberStatus; stage?: number; limit?: number; offset?: number } = {},
): Promise<{ total: number; devices: RolloutDevice[] }> {
  const p = new URLSearchParams();
  if (q.status) p.set('status', q.status);
  if (q.stage !== undefined) p.set('stage', String(q.stage));
  if (q.limit) p.set('limit', String(q.limit));
  if (q.offset) p.set('offset', String(q.offset));
  const s = p.toString();
  return apiFetch(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/devices${s ? `?${s}` : ''}`);
}

export const createRollout = (input: CreateRolloutInput): Promise<{ rollout: Rollout }> =>
  post('/v1/edge/updates/rollouts', input);

export const submitRollout = (id: string): Promise<{ rollout: Rollout }> =>
  post(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/submit`);

export const approveRollout = (
  id: string,
): Promise<{ rollout: Rollout; started: { instructed: number; alreadyCurrent: number } }> =>
  post(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/approve`);

export const pauseRollout = (id: string, reason: string): Promise<{ rollout: Rollout; standDown: StandDownResult }> =>
  post(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/pause`, { reason });

export const resumeRollout = (id: string, reason: string): Promise<{ rollout: Rollout; reinstructed: number }> =>
  post(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/resume`, { reason });

export const cancelRollout = (id: string, reason: string): Promise<{ rollout: Rollout; standDown: StandDownResult }> =>
  post(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/cancel`, { reason });

export const rollbackRollout = (
  id: string,
  reason: string,
): Promise<{
  rollout: Rollout;
  rollbackRequested: number;
  skippedWithdrawn: number;
  standDown: StandDownResult;
  fromVersions: string[];
  publisherCommands: string[];
}> => post(`/v1/edge/updates/rollouts/${encodeURIComponent(id)}/rollback`, { reason });

export const engageUpdateHold = (
  reason: string,
): Promise<{ hold: UpdateHold; rollouts: string[]; standDown: StandDownResult }> => post('/v1/edge/updates/hold', { reason });

export const releaseUpdateHold = (reason: string): Promise<{ hold: UpdateHold; reinstructed: number }> =>
  post('/v1/edge/updates/hold/release', { reason });

export const withdrawVersion = (
  version: string,
  reason: string,
): Promise<{
  version: string;
  reason: string;
  halted: string[];
  cancelled: string[];
  standDown: StandDownResult;
  /** The reason as it is in the command: plain ASCII without quotes, the same in bash and PowerShell. */
  reasonInCommand: string;
  /** Sign into <dir>, then publish onto the downloads folder (publish-withdrawn). */
  publisherCommands: string[];
  signedListPublished: false;
}> => post('/v1/edge/updates/withdrawn', { version, reason });

export const proposeReinstate = (version: string, reason: string): Promise<{ withdrawn: WithdrawnVersion }> =>
  post(`/v1/edge/updates/withdrawn/${encodeURIComponent(version)}/reinstate`, { reason });

export const confirmReinstate = (
  version: string,
): Promise<{
  version: string;
  reason: string;
  proposedBy: string;
  reinstatedBy: string;
  publisherCommands: string[];
  signedListPublished: false;
}> => post(`/v1/edge/updates/withdrawn/${encodeURIComponent(version)}/reinstate/confirm`);

/** Is this caller the one who proposed reinstating? Then a different approver must confirm (the server decides). */
export function isOwnProposal(p: { by: string } | null, actorId: string | null): boolean {
  return p !== null && actorId !== null && p.by.trim().toLowerCase() === actorId.trim().toLowerCase();
}

/** The halted rollouts an operator must decide on (roll back or cancel). */
export function haltedRollouts(rollouts: readonly Rollout[]): Rollout[] {
  return rollouts.filter((r) => r.state === 'halted');
}

// ── words ──────────────────────────────────────────────────────────────────

export const ROLLOUT_STATE_LABEL: Record<RolloutState, string> = {
  draft: 'Draft',
  awaiting_approval: 'Awaiting approval',
  running: 'Running',
  paused: 'Paused',
  halted: 'Halted',
  completed: 'Completed',
  rolled_back: 'Rolled back',
  cancelled: 'Cancelled',
};

export const MEMBER_STATUS_LABEL: Record<RolloutMemberStatus, string> = {
  instructed: 'Told to update',
  updated: 'Updated',
  failed: 'Failed',
  rolled_back: 'Rolled back by itself',
  managed_externally: 'Managed by your own tool',
  stood_down: 'Stood down',
  gone: 'Decommissioned',
  rollback_requested: 'Rollback requested',
  reverted: 'Back on previous version',
  rollback_failed: 'Rollback failed',
};

/** A plain x.y.z, the only form a signed manifest carries. */
export function isPlainVersion(v: string): boolean {
  return /^\d{1,6}\.\d{1,6}\.\d{1,6}$/.test(v.trim());
}

/** Waves as typed ("10, 50, 100") to numbers, or the reason they are not a plan. */
export function parseWaves(text: string): { waves: number[] } | { problem: string } {
  const parts = text.split(/[\s,]+/).filter(Boolean);
  const waves = parts.map(Number);
  if (waves.length < 1 || waves.length > 5) return { problem: 'between one and five waves' };
  let previous = 0;
  for (const w of waves) {
    if (!Number.isInteger(w) || w < 1 || w > 100) return { problem: 'each wave is a whole percentage from 1 to 100' };
    if (w <= previous) return { problem: 'each wave covers more of the fleet than the one before' };
    previous = w;
  }
  if (waves[waves.length - 1] !== 100) return { problem: 'the last wave is 100%' };
  return { waves };
}

/** May this caller approve this rollout? Mirrors the server's four-eyes rule (the server decides). */
export function fourEyesBlocks(r: Pick<Rollout, 'createdBy' | 'submittedBy'>, actorId: string | null): string | null {
  if (!actorId) return null;
  const a = actorId.trim().toLowerCase();
  if (a === r.createdBy.trim().toLowerCase()) return 'You created this rollout; a different person must approve it.';
  if (r.submittedBy && a === r.submittedBy.trim().toLowerCase()) return 'You submitted this rollout; a different person must approve it.';
  return null;
}
